import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, Clock, ClipboardCheck, Search, Pencil, Trash2, Eye, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { SECTIONS } from '@/data/sections.data'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/store/authStore'
import { useValuationStore } from '@/store/valuationStore'
import { useRefStore } from '@/store/refStore'
import { v4 as uuidv4 } from 'uuid'
import { getDashboard } from '@/services/dashboardService'
import { deleteClaimByUserIdAndCaseId, getClaimByUserIdAndCaseId, cleanupOrphanedCases, deleteAllClaimPhotos } from '@/services/claimService'
import { fetchRefSingle } from '@/services/refService'
import { preloadRefCategories } from '@/utils/preloadRefCategories'
import { exportClaimToPdf } from '@/utils/pdf/exportClaimPdf'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useUiStore } from '@/store/uiStore'
import { getMessage } from '@/utils/messages'
import type { DashboardRequest, DashboardResponse, DashboardClaim } from '@/types/dashboard'

const REF_VEHICLE_TYPE = 'vehicle_type'

const PAGE_SIZES = [5, 10, 25, 50]

type SortableColumn =
  | 'claimRefNumber'
  | 'owner'
  | 'status'
  | 'createdDate'
  | 'updatedDate'
  | 'vehicleType'
  | 'useType'


function statusBadgeVariant(statusId: number): 'warning' | 'success' | 'neutral' {
  if (statusId === 1) return 'warning'
  if (statusId === 2) return 'success'
  return 'neutral'
}

function imageUploadStatusVariant(status: string | null): 'warning' | 'success' | 'error' | 'neutral' {
  if (status === 'uploaded') return 'success'
  if (status === 'failed') return 'error'
  if (status === 'pending') return 'warning'
  return 'neutral'
}

function imageUploadStatusLabel(status: string | null): string {
  if (status === 'uploaded') return getMessage('dashboard.image_upload_status_uploaded')
  if (status === 'failed') return getMessage('dashboard.image_upload_status_failed')
  if (status === 'pending') return getMessage('dashboard.image_upload_status_pending')
  return '—'
}

function statusLabel(statusId: number, fallback: string): string {
  if (statusId === 1) return getMessage('dashboard.status_in_progress')
  if (statusId === 2) return getMessage('dashboard.status_submitted')
  if (statusId === 3) return getMessage('dashboard.status_completed')
  return fallback
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return iso
  }
}

export function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { showToast } = useUiStore()
  const { cache: refCache, setCache, getLabelById } = useRefStore()

  const userId = String(user?.userId ?? '')

  const [request, setRequest] = useState<DashboardRequest>({
    userId,
    pageNumber: 1,
    pageSize: 10,
    sortBy: 'updatedDate',
    sortDirection: 'desc',
  })

  const [data, setData] = useState<DashboardResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<DashboardClaim | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [vehicleTypes, setVehicleTypes] = useState<{ id: number; label: string }[]>([])
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load vehicle type ref data
  useEffect(() => {
    const cached = refCache[REF_VEHICLE_TYPE]
    if (cached) {
      setVehicleTypes(cached.filter((o) => o.id != null) as { id: number; label: string }[])
      return
    }
    fetchRefSingle(REF_VEHICLE_TYPE).then((options) => {
      setCache(REF_VEHICLE_TYPE, options)
      setVehicleTypes(options.filter((o) => o.id != null) as { id: number; label: string }[])
    }).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Best-effort cleanup of blob folders left behind by abandoned (unsaved) drafts
  useEffect(() => {
    if (!userId) return
    cleanupOrphanedCases(userId)
  }, [userId])

  const fetchData = useCallback(async (req: DashboardRequest) => {
    setLoading(true)
    try {
      const res = await getDashboard(req)
      setData(res)
    } catch (err) {
      showToast(err instanceof Error ? err.message : getMessage('dashboard.error_load_failed'), 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    fetchData(request)
  }, [request, fetchData])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (data?.summary?.totalClaims === 0) return
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setRequest((prev) => ({
        ...prev,
        pageNumber: 1,
        search: value.trim() || undefined,
      }))
    }, 300)
  }

  const handleTileClick = (statusId: number) => {
    if (data?.summary?.totalClaims === 0) return
    setRequest((prev) => ({ ...prev, pageNumber: 1, status: statusId || undefined }))
  }

  const handleVehicleTypeChange = (value: string) => {
    if (data?.summary?.totalClaims === 0) return
    const vtId = parseInt(value, 10)
    setRequest((prev) => ({ ...prev, pageNumber: 1, vehicleType: vtId || undefined }))
  }

  const handleClearFilters = () => {
    setSearchInput('')
    setRequest({
      userId,
      pageNumber: 1,
      pageSize: request.pageSize,
      sortBy: 'updatedDate',
      sortDirection: 'desc',
    })
  }

  const handleSort = (col: SortableColumn) => {
    setRequest((prev) => ({
      ...prev,
      pageNumber: 1,
      sortBy: col,
      sortDirection: prev.sortBy === col && prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handlePageChange = (page: number) => {
    setRequest((prev) => ({ ...prev, pageNumber: page }))
  }

  const handlePageSizeChange = (size: number) => {
    setRequest((prev) => ({ ...prev, pageSize: size, pageNumber: 1 }))
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await deleteClaimByUserIdAndCaseId(userId, deleteTarget.caseId)
      if (res.success) {
        showToast(getMessage('dashboard.toast_delete_success'), 'success')
        setDeleteTarget(null)
        fetchData(request)
      } else {
        showToast(res.message ?? getMessage('dashboard.toast_delete_failed'), 'error')
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : getMessage('dashboard.toast_delete_failed'), 'error')
    } finally {
      setDeleting(false)
    }
  }

  const handleNewInspection = () => {
    const store = useValuationStore.getState()
    const abandonedCaseId = store.caseId
    store.photos.forEach((p) => URL.revokeObjectURL(p.blobUrl))
    store.clearLocalDraft()
    store.setCaseId(uuidv4())
    useValuationStore.setState({
      isDirty: false,
      hasTriedSubmit: false,
      hasTriedSave: false,
    })
    store.markHydrated()
    navigate('/form')

    // If the draft being abandoned was never saved, delete its blob folder now
    // instead of waiting for the next Dashboard-load cleanup.
    if (abandonedCaseId) {
      getClaimByUserIdAndCaseId(userId, abandonedCaseId).then((res) => {
        if (!res.success) {
          deleteAllClaimPhotos(userId, abandonedCaseId)
        }
      })
    }
  }

  const handleEdit = (claim: DashboardClaim) => {
    const store = useValuationStore.getState()
    store.photos.forEach((p) => URL.revokeObjectURL(p.blobUrl))
    useValuationStore.setState({
      photos: [],
      formValues: {},
      isDirty: false,
      hasTriedSubmit: false,
      hasTriedSave: false,
      status: 'draft',
      sectionStates: Object.fromEntries(SECTIONS.map((s) => [s.id, { expanded: s.id === 'basic-information', complete: false, touched: false }])),
    })
    navigate('/form', { state: { userId, caseId: claim.caseId } })
  }

  const handleView = (claim: DashboardClaim) => {
    navigate(`/claim/${claim.caseId}/view`, { state: { userId, caseId: claim.caseId } })
  }

  const [exportingCaseId, setExportingCaseId] = useState<string | null>(null)

  const handleExport = async (claim: DashboardClaim) => {
    setExportingCaseId(claim.caseId)
    try {
      const res = await getClaimByUserIdAndCaseId(userId, claim.caseId)
      if (!res.success || !res.data) {
        throw new Error(res.message ?? getMessage('dashboard.export_error'))
      }
      await preloadRefCategories(res.data)
      await exportClaimToPdf(res.data, getLabelById)
    } catch (err) {
      showToast(err instanceof Error ? err.message : getMessage('dashboard.export_error'), 'error')
    } finally {
      setExportingCaseId(null)
    }
  }

  const summary = data?.summary
  const pagination = data?.pagination
  const claims = data?.claims ?? []

  const activeStatus = request.status ?? 0

  const summaryTiles = [
    {
      label: getMessage('dashboard.tile_total_claims'),
      value: summary?.totalClaims ?? 0,
      color: 'var(--accent)',
      bg: 'rgba(0,121,189,0.08)',
      border: 'rgba(0,121,189,0.2)',
      Icon: ClipboardList,
      statusId: 0,
    },
    {
      label: getMessage('dashboard.tile_in_progress'),
      value: summary?.draftClaims ?? 0,
      color: 'var(--accent2)',
      bg: 'rgba(224,123,16,0.08)',
      border: 'rgba(224,123,16,0.2)',
      Icon: Clock,
      statusId: 1,
    },
    {
      label: getMessage('dashboard.tile_submitted'),
      value: summary?.pendingClaims ?? 0,
      color: 'var(--success)',
      bg: 'rgba(22,163,74,0.08)',
      border: 'rgba(22,163,74,0.2)',
      Icon: ClipboardCheck,
      statusId: 2,
    },
  ]

  const startRecord = pagination ? (pagination.pageNumber - 1) * pagination.pageSize + 1 : 0
  const endRecord = pagination ? Math.min(pagination.pageNumber * pagination.pageSize, pagination.totalRecords) : 0

  const SortIcon = ({ col }: { col: SortableColumn }) => {
    if (request.sortBy !== col) return <span className="opacity-50 ml-1">↕</span>
    return <span className="ml-1 text-[var(--accent)]">{request.sortDirection === 'asc' ? '↑' : '↓'}</span>
  }

  const thClass = 'px-4 py-3 text-left text-[13px] font-bold tracking-[0.2px] text-black whitespace-nowrap'
  const thStyle: React.CSSProperties = { fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }
  const thSortClass = `${thClass} cursor-pointer select-none hover:text-[var(--accent)] transition-colors duration-150`
  const btnBase = cn(
    'flex items-center gap-1.5 px-3 py-[6px] rounded-[var(--radius-sm)]',
    'border border-[var(--border)] bg-[var(--surface2)]',
    'text-[var(--text-dim)] text-[12px] font-semibold',
    'hover:border-[var(--accent)] hover:text-[var(--accent)]',
    'hover:bg-[rgba(61,142,240,0.05)] transition-all duration-[180ms]'
  )

  const hasActiveFilters = !!(request.search || request.status || request.vehicleType)

  return (
    <main className="px-4 lg:px-10 xl:px-20 py-5 min-h-0 relative max-w-[1400px] mx-auto w-full">

      {/* Full-page loading overlay — blocks all interaction while fetching */}
      {loading && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-white/30 backdrop-blur-[1.5px]">
          <LoadingSpinner size="lg" label={getMessage('dashboard.loading_claims')} />
        </div>
      )}

      {/* Full-page loading overlay — blocks all interaction while generating a PDF export */}
      {exportingCaseId && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-white/30 backdrop-blur-[1.5px]">
          <LoadingSpinner size="lg" label={getMessage('dashboard.export_generating')} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-[20px] font-bold text-[var(--text)]">{getMessage('dashboard.title')}</h1>
        <button
          type="button"
          data-tour="new-claim-button"
          onClick={handleNewInspection}
          className={cn(
            'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
            'border border-[var(--border)] bg-[var(--surface2)]',
            'text-[var(--text-dim)] text-[12px] font-semibold flex-shrink-0',
            'hover:border-[var(--accent)] hover:text-[var(--accent)]',
            'hover:bg-[rgba(61,142,240,0.05)] transition-all duration-[180ms]'
          )}
        >
          {getMessage('dashboard.button_new_inspection')}
        </button>
      </div>

      {/* Summary tiles — always 3 columns; stacked below md, side-by-side at md+ */}
      <div data-tour="summary-tiles" className="grid grid-cols-3 gap-2 md:gap-4 mb-5">
        {summaryTiles.map((tile) => {
          const noRecords = (summary?.totalClaims ?? 0) === 0
          const isActive = !noRecords && activeStatus === tile.statusId
          return (
          <button
            key={tile.label}
            type="button"
            disabled={noRecords}
            onClick={() => handleTileClick(tile.statusId)}
            aria-pressed={isActive}
            className={cn(
              'rounded-[var(--radius)] p-2.5 md:p-4 flex flex-col md:flex-row items-center md:items-center gap-1 md:gap-4',
              'text-left transition-all duration-150 w-full',
              noRecords ? 'cursor-default' : 'cursor-pointer',
              !isActive && !noRecords && 'hover:opacity-80'
            )}
            style={{
              background: tile.bg,
              border: `1px solid ${tile.border}`,
              ...(isActive ? { boxShadow: `0 0 0 2px ${tile.color}` } : {}),
            }}
          >
            <div
              className="w-7 h-7 md:w-10 md:h-10 rounded-[8px] flex items-center justify-center flex-shrink-0"
              style={{ background: `color-mix(in srgb, ${tile.color} 20%, transparent)` }}
            >
              <tile.Icon size={15} style={{ color: tile.color }} className="md:hidden" />
              <tile.Icon size={20} style={{ color: tile.color }} className="hidden md:block" />
            </div>
            <div className="text-center md:text-left">
              <div className="text-[10px] md:text-[12px] font-semibold text-[var(--text-dim)] tracking-[0.3px] mb-0.5 leading-tight whitespace-nowrap">
                {tile.label}
              </div>
              <div className="text-[18px] md:text-[30px] font-bold leading-none" style={{ color: tile.color }}>
                {tile.value}
              </div>
            </div>
          </button>
          )
        })}
      </div>

      {/* Search + filters card */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] px-4 py-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">

          {/* Search input — full width on mobile, equal share on desktop */}
          <div data-tour="search-box" className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)] pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={getMessage('dashboard.search_placeholder')}
              className="field-input !pl-8"
            />
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-[var(--border)] hidden sm:block" />

          {/* Vehicle type filter */}
          <div data-tour="vehicle-type-filter" className="floating-wrapper !mt-0 w-full sm:flex-1 sm:min-w-[160px]">
            <select
              value={request.vehicleType ?? 0}
              onChange={(e) => handleVehicleTypeChange(e.target.value)}
              className="field-input floating-input"
            >
              <option value={0}>{getMessage('dashboard.filter_all_vehicle_types')}</option>
              {vehicleTypes.map((vt) => (
                <option key={vt.id} value={vt.id}>{vt.label}</option>
              ))}
            </select>
            <label className="floating-label label-float-up">{getMessage('dashboard.filter_vehicle_type_label')}</label>
          </div>

          {/* Clear filters — ml-auto pushes right on the same row as filters */}
          {hasActiveFilters && (
            <button onClick={handleClearFilters} className={cn(btnBase, 'ml-auto')}>
              {getMessage('dashboard.button_clear_filters')}
            </button>
          )}
        </div>
      </div>

      {/* Claims table (+ pagination below, both spotlighted together during the tour) */}
      <div data-tour="claims-table">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden relative">

        {!loading && claims.length === 0 ? (
          <EmptyState
            icon="📋"
            title={getMessage('dashboard.empty_title')}
            description={getMessage('dashboard.empty_description')}
          />
        ) : (
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full lg:min-w-[1040px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#d9edf7]">
                  <th onClick={() => handleSort('claimRefNumber')} className={thSortClass} style={thStyle}>
                    {getMessage('dashboard.col_claim_ref')}<SortIcon col="claimRefNumber" />
                  </th>
                  <th onClick={() => handleSort('owner')} className={cn(thSortClass, 'hidden lg:table-cell')} style={thStyle}>
                    {getMessage('dashboard.col_owner')}<SortIcon col="owner" />
                  </th>
                  <th onClick={() => handleSort('useType')} className={cn(thSortClass, 'hidden lg:table-cell')} style={thStyle}>
                    {getMessage('dashboard.col_use_type')}<SortIcon col="useType" />
                  </th>
                  <th onClick={() => handleSort('createdDate')} className={cn(thSortClass, 'hidden lg:table-cell')} style={thStyle}>
                    {getMessage('dashboard.col_created_date')}<SortIcon col="createdDate" />
                  </th>
                  <th className={cn(thClass, 'hidden lg:table-cell')} style={thStyle}>{getMessage('dashboard.col_submitted_date')}</th>
                  <th onClick={() => handleSort('status')} className={thSortClass} style={thStyle}>
                    {getMessage('dashboard.col_status')}<SortIcon col="status" />
                  </th>
                  <th className={cn(thClass, 'hidden lg:table-cell')} style={thStyle}>{getMessage('dashboard.col_file_status')}</th>
                  <th className={cn(thClass, 'w-[88px] md:w-[120px] lg:w-[168px]')} style={thStyle}>{getMessage('dashboard.col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => (
                  <tr
                    key={claim.caseId}
                    className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--surface2)] transition-colors duration-100"
                  >
                    <td className="px-4 py-3">
                      <span className="text-[13px] text-[var(--text)]">{claim.claimRefNumber}</span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3">
                      <span className="text-[13px] text-[var(--text)]">{claim.owner || '—'}</span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3">
                      <span className="text-[13px] text-[var(--text)]">{claim.useType || '—'}</span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3">
                      <span className="text-[13px] text-[var(--text)]">{formatDate(claim.createdDate)}</span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3">
                      <span className="text-[13px] text-[var(--text)]">{formatDate(claim.submittedDate)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge label={statusLabel(claim.statusId, claim.status ?? '')} variant={statusBadgeVariant(claim.statusId)} />
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3">
                      {claim.imageUploadStatus ? (
                        <Badge label={imageUploadStatusLabel(claim.imageUploadStatus)} variant={imageUploadStatusVariant(claim.imageUploadStatus)} />
                      ) : (
                        <span className="text-[13px] text-[var(--text-muted)]">{getMessage('dashboard.no_files')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-nowrap items-center gap-1.5">
                        {claim.statusId === 1 ? (
                          <>
                            {/* Edit */}
                            <button
                              onClick={() => handleEdit(claim)}
                              title={getMessage('dashboard.action_edit')}
                              aria-label={getMessage('dashboard.action_edit')}
                              className={cn(btnBase, 'px-2 lg:px-3')}
                            >
                              <Pencil size={13} />
                              <span className="hidden lg:inline">{getMessage('dashboard.action_edit')}</span>
                            </button>
                            {/* Delete */}
                            <button
                              onClick={() => setDeleteTarget(claim)}
                              title={getMessage('dashboard.action_delete')}
                              aria-label={getMessage('dashboard.action_delete')}
                              className={cn(
                                'flex items-center gap-1.5 px-2 lg:px-3 py-[6px] rounded-[var(--radius-sm)]',
                                'border border-[var(--border)] bg-[var(--surface2)]',
                                'text-[var(--error)] text-[12px] font-semibold',
                                'hover:border-[var(--error)] hover:bg-[rgba(220,38,38,0.05)]',
                                'transition-all duration-[180ms]'
                              )}
                            >
                              <Trash2 size={13} />
                              <span className="hidden lg:inline">{getMessage('dashboard.action_delete')}</span>
                            </button>
                          </>
                        ) : (
                          <>
                            {/* View */}
                            <button
                              onClick={() => handleView(claim)}
                              title={getMessage('dashboard.action_view')}
                              aria-label={getMessage('dashboard.action_view')}
                              className={cn(btnBase, 'px-2 lg:px-3')}
                            >
                              <Eye size={13} />
                              <span className="hidden lg:inline">{getMessage('dashboard.action_view')}</span>
                            </button>
                            {/* Export — Submitted claims only */}
                            {claim.statusId === 2 && (
                              <button
                                onClick={() => handleExport(claim)}
                                disabled={!!exportingCaseId}
                                title={getMessage('dashboard.action_export')}
                                aria-label={getMessage('dashboard.action_export')}
                                className={cn(btnBase, 'px-2 lg:px-3', !!exportingCaseId && 'opacity-60 cursor-not-allowed')}
                              >
                                <Download size={13} />
                                <span className="hidden lg:inline">{getMessage('dashboard.action_export')}</span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalRecords > 0 && (
        <div className="flex items-center justify-between mt-4 gap-2 sm:gap-4 flex-nowrap">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <span className="text-[11px] sm:text-[12px] text-[var(--text-muted)] whitespace-nowrap">
              <span className="sm:hidden">{getMessage('dashboard.pagination_rows_per_page_short')}</span>
              <span className="hidden sm:inline">{getMessage('dashboard.pagination_rows_per_page')}</span>
            </span>
            <select
              value={request.pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              title={getMessage('dashboard.pagination_rows_per_page')}
              aria-label={getMessage('dashboard.pagination_rows_per_page')}
              className="field-input !w-auto flex-shrink-0 !py-1 !pl-1.5 !text-[11px] sm:!py-[9px] sm:!pl-3 sm:!text-[12.5px]"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span className="text-[11px] sm:text-[12px] text-[var(--text-muted)] whitespace-nowrap truncate">
              {startRecord}–{endRecord} of {pagination.totalRecords}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              disabled={request.pageNumber <= 1}
              onClick={() => handlePageChange(request.pageNumber - 1)}
              title={getMessage('dashboard.pagination_prev')}
              aria-label={getMessage('dashboard.pagination_prev')}
              className={cn(
                btnBase,
                'px-2 lg:px-3',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[var(--border)] disabled:hover:text-[var(--text-dim)] disabled:hover:bg-[var(--surface2)]'
              )}
            >
              <ChevronLeft size={13} className="lg:hidden" />
              <span className="hidden lg:inline">{getMessage('dashboard.pagination_prev')}</span>
            </button>
            <span className="text-[12px] text-[var(--text-muted)] whitespace-nowrap">
              {getMessage('dashboard.pagination_page_of').replace('{page}', String(request.pageNumber)).replace('{total}', String(pagination.totalPages))}
            </span>
            <button
              disabled={request.pageNumber >= pagination.totalPages}
              onClick={() => handlePageChange(request.pageNumber + 1)}
              title={getMessage('dashboard.pagination_next')}
              aria-label={getMessage('dashboard.pagination_next')}
              className={cn(
                btnBase,
                'px-2 lg:px-3',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[var(--border)] disabled:hover:text-[var(--text-dim)] disabled:hover:bg-[var(--surface2)]'
              )}
            >
              <span className="hidden lg:inline">{getMessage('dashboard.pagination_next')}</span>
              <ChevronRight size={13} className="lg:hidden" />
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        loading={deleting}
        title={getMessage('dashboard.delete_title')}
        message={
          deleteTarget
            ? (() => {
                const [before, after] = getMessage('dashboard.delete_message').split('{claimRef}')
                return (
                  <>
                    {before}
                    <strong>{deleteTarget.claimRefNumber}</strong>
                    {after}
                  </>
                )
              })()
            : ''
        }
        confirmLabel={deleting ? getMessage('dashboard.delete_confirm_loading') : getMessage('dashboard.delete_confirm')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </main>
  )
}
